import {
  IonContent,
  IonFab,
  IonFabButton,
  IonLoading,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonRouterLink,
  useIonAlert
} from '@ionic/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

import { Empty, Padding, Spacer, Tips } from '@/components/common';
import {
  CalendarPopover,
  DashboardHeader,
  DatePicker,
  EditProceduresModal,
  SharePrompt,
  Timeline
} from '@/components/dashboard';
import { Icon, Text } from '@/components/memo';
import {
  useAuth,
  useClinic,
  useDentist,
  useRequests,
  useShare
} from '@/contexts';
import { Request } from '@/data/Requests';
import { uploadNotificationToken } from '@/data/Users';
import { withCalendarModals, withSelectedDate } from '@/hocs';
import { useToast } from '@/hooks';
import {
  ACCESS_TYPES,
  HELP_URL,
  OFF_DUTY_OPTIONS,
  REFRESH_TIMEOUT_IN_MS,
  REQUEST_STATUS_OPTIONS
} from '@/utils/constants';
import { now } from '@/utils/helpers/now';
import { parseDate } from '@/utils/helpers/parseDate';
import { parseTime } from '@/utils/helpers/parseTime';
import {
  NOTIFICATION_TOKEN_KEY,
  registerNotifications
} from '@/utils/notifications';
import { TABS } from '@/utils/tabs';
import {
  differenceInMinutes,
  format,
  isAfter,
  isEqual,
  isSameDay,
  isValid,
  startOfDay,
  subDays
} from 'date-fns';
import {
  addOutline,
  calendarOutline,
  flagOutline,
  partlySunnyOutline,
  refreshOutline,
  shareOutline,
  swapHorizontalOutline
} from 'ionicons/icons';
import { useLocation } from 'react-router-dom';

import './Calendar.css';

interface CalendarProps {
  selectedDate: Date;
  setSelectedDate: React.Dispatch<React.SetStateAction<Date>>;
  modals: {
    manualBooking: { isOpen: boolean; onOpen: () => void; onClose: () => void };
    offDuty: { isOpen: boolean; onOpen: () => void; onClose: () => void };
  };
  setRequestToReschedule: (request: Request | null) => void;
}

const Calendar: React.FC<CalendarProps> = ({
  selectedDate,
  setSelectedDate,
  modals,
  setRequestToReschedule
}) => {
  const { user } = useAuth();
  const { isClinic } = useClinic();
  const {
    selectedDentist: dentist,
    dentists,
    onSwitch,
    updateCalendarTipsMutation
  } = useDentist();
  const {
    requests,
    setFocusedRequestId,
    markAsOffDutyMutation,
    fetchAppointmentsForDate,
    isDateLoaded,
    isDateLoading,
    isDateFailed
  } = useRequests();
  const { onShare } = useShare();
  const displayToast = useToast();
  const [presentAlert] = useIonAlert();

  const { search } = useLocation();
  const focusedRequestIdFromUrl = new URLSearchParams(search).get(
    'focusedRequestId'
  );
  const selectedDateFromUrl = new URLSearchParams(search).get('selectedDate');

  const [month, setMonth] = useState<string>(format(now(), 'MMMM yyyy'));
  const [isCalendarPopoverOpen, setIsCalendarPopoverOpen] = useState(false);
  const [offDutyLoading, setOffDutyLoading] = useState<boolean>(false);
  const [requestToEditProcedures, setRequestToEditProcedures] =
    useState<Request | null>(null);

  const page = useRef<HTMLElement>();

  const isNativePlatform = Capacitor.isNativePlatform();

  const hasAccess = useMemo(() => {
    return isClinic || dentist?.access === ACCESS_TYPES.EDIT;
  }, [isClinic, dentist?.access]);

  const appointmentsForSelectedDate = useMemo(() => {
    const selectedDateString = format(selectedDate, 'yyyy-MM-dd');

    const dateAppointments = requests.filter(
      (request) => request.date === selectedDateString
    );

    const appointments = dateAppointments.filter(
      (request) =>
        request.status === REQUEST_STATUS_OPTIONS.APPROVED ||
        request.status === REQUEST_STATUS_OPTIONS.RESCHEDULED
    );

    return appointments.sort((a, b) => {
      const startTimeDiff = differenceInMinutes(
        parseTime(a.start_time),
        parseTime(b.start_time)
      );
      return startTimeDiff === 0
        ? differenceInMinutes(parseTime(a.end_time), parseTime(b.end_time))
        : startTimeDiff;
    });
  }, [selectedDate, requests]);

  const isEmpty = useMemo(() => {
    const selectedDateString = format(selectedDate, 'yyyy-MM-dd');

    // If the date is currently loading, don't show empty state
    if (isDateLoading(selectedDateString)) return false;

    return (
      (isDateLoaded(selectedDateString) ||
        isDateFailed(selectedDateString)) &&
      appointmentsForSelectedDate.length === 0
    );
  }, [
    selectedDate,
    appointmentsForSelectedDate,
    isDateLoading,
    isDateLoaded,
    isDateFailed
  ]);

  const onRefresh = useCallback(
    async (event: CustomEvent) => {
      setTimeout(async () => {
        const dateString = format(selectedDate, 'yyyy-MM-dd');
        await fetchAppointmentsForDate(dateString);
        event.detail.complete();
      }, REFRESH_TIMEOUT_IN_MS);
    },
    [selectedDate, fetchAppointmentsForDate]
  );

  const onSetMonth = (month: string) => setMonth(month);

  const onSelect = useCallback(
    async (date: Date = startOfDay(now())) => {
      setIsCalendarPopoverOpen(false);

      const dateString = format(date, 'yyyy-MM-dd');
      if (
        isDateLoading(dateString) ||
        isDateLoaded(dateString) ||
        isDateFailed(dateString)
      ) {
        return;
      }

      try {
        await fetchAppointmentsForDate(dateString);
      } catch {
        displayToast({
          message: `Please check your internet connection.`,
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'tab-bar'
        });
      }
    },
    [isDateLoaded, isDateLoading, isDateFailed, fetchAppointmentsForDate]
  );

  const onSelectDate = useCallback(
    (e: CustomEvent) => {
      setSelectedDate(startOfDay(parseDate(e.detail.value)));
    },
    [setSelectedDate]
  );

  const onOpenCalendarPopover = () => {
    setIsCalendarPopoverOpen(true);
  };

  const onCloseCalendarPopover = () => {
    setIsCalendarPopoverOpen(false);
  };

  const onToday = () => {
    if (isSameDay(startOfDay(now()), selectedDate)) {
      setSelectedDate(startOfDay(subDays(now(), 1)));
      setSelectedDate(startOfDay(now()));
      displayToast({
        message: "You are already on today's date.",
        duration: 2400,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
      return;
    }
    setSelectedDate(startOfDay(now()));
  };

  const onHelp = async () => {
    await Browser.open({ url: HELP_URL });
  };

  const onHideTips = async () => {
    if (dentist?.id) {
      updateCalendarTipsMutation.mutate(dentist.id);
    }
  };

  const markAsOffDuty = useCallback(async () => {
    await presentAlert({
      header: 'Mark as off-duty',
      message: `This will prevent appointments from being booked on ${format(selectedDate, 'MMMM d, yyyy')}.`,
      inputs: [
        {
          type: 'radio',
          label: 'Block the entire day',
          value: OFF_DUTY_OPTIONS.WHOLE_DAY,
          checked: true
        },
        {
          type: 'radio',
          label: 'Block a specific time',
          value: OFF_DUTY_OPTIONS.TIME_RANGE
        }
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel'
        },
        {
          text: 'Confirm',
          handler: async (selectedMethod: string) => {
            if (selectedMethod === OFF_DUTY_OPTIONS.WHOLE_DAY) {
              await markAsOffDutyMutation.mutateAsync({ date: selectedDate });
              displayToast({
                message: `Marked ${format(selectedDate, 'MMMM d, yyyy')} as off-duty.`,
                duration: 2400,
                position: 'bottom',
                positionAnchor: 'tab-bar'
              });
              return;
            }

            if (selectedMethod === OFF_DUTY_OPTIONS.TIME_RANGE) {
              modals.offDuty.onOpen();
              return;
            }
          }
        }
      ]
    });
  }, [selectedDate, markAsOffDutyMutation]);

  const tips = [
    'Experience Odonto in action—try making a test booking!',
    <>
      Need help transferring all your appointments to Odonto?{' '}
      <IonRouterLink onClick={onHelp}>Contact us</IonRouterLink>.
    </>,
    'For a seamless experience, use Odonto for all your appointments.'
  ];

  /* Load the appointments for the selected date when the date changes */
  useEffect(() => {
    onSelect(selectedDate);
  }, [selectedDate, onSelect]);

  /* Enable notifications and save the token */
  useEffect(() => {
    const enableNotifications = async (userId: string) => {
      const { value: localNotificationToken } = await Preferences.get({
        key: NOTIFICATION_TOKEN_KEY
      });

      if (localNotificationToken) {
        return;
      }

      const notificationToken = await registerNotifications();

      if (notificationToken) {
        try {
          await uploadNotificationToken({
            userId: userId,
            token: notificationToken
          });

          await Preferences.set({
            key: NOTIFICATION_TOKEN_KEY,
            value: notificationToken
          });
        } catch {}
      }
    };

    if (isNativePlatform && user?.id) {
      enableNotifications(user.id);
    }
  }, []);

  useEffect(() => {
    return () => setOffDutyLoading(false);
  }, []);

  useEffect(() => {
    if (focusedRequestIdFromUrl) {
      setFocusedRequestId(focusedRequestIdFromUrl);
    } else {
      setFocusedRequestId(undefined);
    }
  }, [focusedRequestIdFromUrl]);

  useEffect(() => {
    if (selectedDateFromUrl && isValid(parseDate(selectedDateFromUrl))) {
      setSelectedDate(startOfDay(parseDate(selectedDateFromUrl)));
    }
  }, [selectedDateFromUrl]);

  return (
    <IonPage id="calendar" ref={page}>
      <IonContent>
        <IonRefresher
          id="calendar-refresher"
          slot="fixed"
          onIonRefresh={onRefresh}
        >
          <IonRefresherContent
            pullingIcon={refreshOutline}
            refreshingSpinner="crescent"
          />
        </IonRefresher>

        <Padding>
          <div className="calendar__fixed-header">
            <DashboardHeader
              title={
                <>
                  {month.split(' ')[0]}{' '}
                  <Text color="primary">{month.split(' ')[1]}</Text>
                </>
              }
              tab={TABS.CALENDAR}
            />

            <div className="calendar__date-picker">
              <DatePicker
                onSelect={setSelectedDate}
                onSetMonth={onSetMonth}
                value={selectedDate}
              />
            </div>

            <Spacer top={8} bottom={8}>
              <div className="calendar__date-picker-buttons">
                <div className="calendar__date-picker-button" onClick={onToday}>
                  <Icon icon={partlySunnyOutline} />
                  <p className="calendar__date-picker-button-text">Today</p>
                </div>
                <div
                  className="calendar__date-picker-button"
                  onClick={onOpenCalendarPopover}
                >
                  <Icon icon={calendarOutline} />
                  <p className="calendar__date-picker-button-text">
                    Select Date
                  </p>
                </div>
                {dentists.length > 1 ? (
                  <div
                    className="calendar__date-picker-button"
                    onClick={onSwitch}
                  >
                    <Icon icon={swapHorizontalOutline} />
                    <p className="calendar__date-picker-button-text">
                      Switch {isClinic ? 'Dentist' : 'Clinic'}
                    </p>
                  </div>
                ) : (
                  <div
                    className="calendar__date-picker-button"
                    onClick={onShare}
                  >
                    <Icon icon={shareOutline} />
                    <p className="calendar__date-picker-button-text">
                      Share Booking Link
                    </p>
                  </div>
                )}
              </div>
            </Spacer>
          </div>

          <Spacer top={194} bottom={dentist?.show_calendar_tips ? 12 : 0}>
            {dentist?.show_calendar_tips && (
              <Tips content={tips} onHide={onHideTips} />
            )}
          </Spacer>

          <Spacer top={isEmpty ? 72 : 12} bottom={isEmpty ? 72 : 24}>
            {isEmpty ? (
              <>
                <Empty
                  icon={calendarOutline}
                  message={`No appointments for ${format(selectedDate, 'MMMM d, yyyy')}`}
                  marginTop={0}
                />
                <Spacer top={32} />
                <SharePrompt />
                {hasAccess &&
                  isNativePlatform &&
                  (isAfter(selectedDate, startOfDay(now())) ||
                    isEqual(selectedDate, startOfDay(now()))) && (
                    <div className="calendar__off-duty">
                      <IonRouterLink onClick={markAsOffDuty}>
                        <Icon icon={flagOutline} />
                        Mark as Off-Duty
                      </IonRouterLink>
                    </div>
                  )}
              </>
            ) : (
              <Timeline
                requests={appointmentsForSelectedDate}
                loading={
                  isDateLoading(format(selectedDate, 'yyyy-MM-dd')) ||
                  (!isDateLoaded(format(selectedDate, 'yyyy-MM-dd')) &&
                    !isDateFailed(format(selectedDate, 'yyyy-MM-dd')))
                }
                setRequestToReschedule={setRequestToReschedule}
                setRequestToEditProcedures={setRequestToEditProcedures}
              />
            )}
          </Spacer>
        </Padding>

        <CalendarPopover
          isOpen={isCalendarPopoverOpen}
          onDidDismiss={onCloseCalendarPopover}
          onSelectDate={onSelectDate}
        />

        <EditProceduresModal
          isOpen={!!requestToEditProcedures}
          onClose={() => setRequestToEditProcedures(null)}
          request={requestToEditProcedures}
          presentingElement={undefined}
        />

        {hasAccess && isNativePlatform && (
          <>
            <IonFab vertical="bottom" horizontal="end" slot="fixed">
              <IonFabButton onClick={modals.manualBooking.onOpen}>
                <Icon icon={addOutline} />
              </IonFabButton>
            </IonFab>
          </>
        )}

        <IonLoading
          isOpen={
            offDutyLoading ||
            updateCalendarTipsMutation.isPending ||
            markAsOffDutyMutation.isPending
          }
          message="Loading..."
          spinner="crescent"
        />
      </IonContent>
    </IonPage>
  );
};

export default withSelectedDate(withCalendarModals(Calendar));
